import React, {createContext, Fragment, useEffect, useMemo, useRef, useState} from 'react'
import useNavigation from '@hook/useNavigation'
import IconNotification from '@svg/gnb/ic_alarm_on.svg'
import IconNotificationMobile from '@svg/gnb/ic_mo_alarm_on.svg'
import WizIconButton from '@component/buttons/WizIconButton'
import UseQueryNotification from '@hook/query/account/useQueryNotification'
import {INavNotification} from '@api/user/account/notification/getNotification'
import {useTranslation} from '@lib/intl/useTranslations'
import {QueryCacheKeys} from '@hook/query/queryKeys'
import {apiReadAllNotification} from '@api/user/account/notification/apiReadAllNotification'
import {useQueryClient} from '@tanstack/react-query'
import {TIME_DAY} from '@constant/time'
import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    offset,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react'
import Text from '@component/Text/Text'
import IconSetting from '@svg/common/ic_setting.svg'
import IconClose from '@svg/common/ic_close.svg'
import {isNotEmptyArray} from '@util/array'
import NewNotificationList from '@component/header/GlobalNavigation/NotificationDropdown/NewNotificationList'
import EarlierNotificationList from '@component/header/GlobalNavigation/NotificationDropdown/EarlierNotificationList'
import EmptyNotificationContainer from '@feature/account/pages/notifications/EmptyNotificationContainer/EmptyNotificationContainer'
import IconDown from '@svg/common/ic_btn_down.svg'
import useQueryFetchMe from '@hook/query/account/useQueryFetchMe'
import DailyReportModal from '@feature/common/components/DailyReportModal/DailyReportModal'
import useQueryGetNotificationCount from '@hook/query/account/notification/useQueryGetNotificationCount'
import WizIcon from '@component/images/WizIcon'
import useWindowSize from '@hook/useWindowSize'
import {createLog} from '@util/logs'
import NotificationCount from '@component/header/GlobalNavigation/NotificationDropdown/NotificationCount'

interface IProps {
    className?: string
    iconColor?: string
    onClickTrigger?: boolean
    setOnClickTrigger?: () => void
}

export const NotificationPopoverContext = createContext({
    setIsOpen: (isOpen: boolean) => {},
    dailyReportOpen: (id: string) => {},
})

const NotificationDropdown = ({className, iconColor, onClickTrigger, setOnClickTrigger}: IProps) => {
    const t = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const queryClient = useQueryClient()
    const {goNotification} = useNavigation()
    const {data: me} = useQueryFetchMe(undefined, false)
    const {windowSize, isSm} = useWindowSize()
    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            flip(),
            shift(),
            offset({mainAxis: isSm ? 0 : 20, crossAxis: isSm ? -25 : -20}),
            size({
                apply({availableWidth, availableHeight, elements}) {
                    // Do things with the data, e.g.

                    Object.assign(elements.floating.style, {
                        width: `${windowSize?.width > 820 ? 820 : windowSize?.width}px`,
                    })
                },
            }),
        ],
        whileElementsMounted: autoUpdate,
    })

    const click = useClick(context)
    const dismiss = useDismiss(context)
    const role = useRole(context)

    const dailyReportModalRef = useRef<any>()

    const {getReferenceProps, getFloatingProps} = useInteractions([click, dismiss, role])

    const {data: notificationPages, refetch, fetchNextPage, isReachingEnd} = UseQueryNotification(me?.id, isOpen)
    const {refetch: refetchNotificationCount} = useQueryGetNotificationCount()

    const notificationList = useMemo(() => {
        let list: INavNotification[] = []
        notificationPages?.pages?.map(page => page?.map(item => list.push(item)))
        return list
    }, [notificationPages])

    const newNotificationList = useMemo(() => {
        return notificationList?.filter(item => Date.now() / 1000 - TIME_DAY < item.created_at)
    }, [notificationList])

    const earlierNotificationList = useMemo(() => {
        return notificationList?.filter(item => item.created_at < Date.now() / 1000 - TIME_DAY)
    }, [notificationList])

    const onClickReadAll = async () => {
        createLog('event', 'navigation_notification_read_all')
        const {data} = await apiReadAllNotification()
        if (data) {
            await queryClient.invalidateQueries({queryKey: QueryCacheKeys.notification.getNotificationsList(me?.id)})
            await refetchNotificationCount()
        }

        setIsOpen(!isOpen)
    }

    const dailyReportOpen = (id: string) => {
        createLog('event', 'navigation_notification_open_daily_report')
        dailyReportModalRef?.current?.show(id)
    }

    useEffect(() => {
        if (isOpen === false && onClickTrigger === true) {
            setIsOpen(!isOpen)
        } else if (isOpen === true && onClickTrigger === true) {
            setOnClickTrigger()
        }
    }, [onClickTrigger, isOpen, setOnClickTrigger])

    return (
        <NotificationPopoverContext.Provider
            value={{
                setIsOpen,
                dailyReportOpen,
            }}>
            <>
                <div ref={refs?.setReference} {...getReferenceProps()} className={`${className}`}>
                    <WizIcon
                        className={`p-[6px] md:p-[0px] relative hover:bg-transparent01 dark:hover:bg-dark_transparent01 md:hover:bg-transparent ${iconColor} cursor-pointer`}
                        onClick={() => {
                            createLog('event', 'navigation_click_notification_icon')
                            setIsOpen(!isOpen)
                        }}>
                        <>
                            <div className={'relative flex'}>
                                <IconNotification width={16} className={`w-[16px] md:hidden`} />
                                <IconNotificationMobile width={24} className={`hidden md:block`} />
                            </div>
                            <NotificationCount />
                        </>
                    </WizIcon>
                </div>
                {isOpen && (
                    <>
                        <FloatingFocusManager context={context} modal={false}>
                            <div
                                ref={refs.setFloating}
                                style={floatingStyles}
                                className={
                                    'sm:!fixed z-sticky lg:!w-full lg:!top-[50px] sm:!top-0 lg:!left-[50%] lg:!translate-x-[-50%]'
                                }
                                {...getFloatingProps()}>
                                <div
                                    className={
                                        'bg-white dark:bg-bg_dark_popup rounded-[5px] shadow-hover lg:mx-auto md:mx-[20px] sm:mx-[0px] min-h-[336px] sm:min-h-[100vh] w-[800px] lg:w-[91vw] sm:w-full'
                                    }>
                                    <div className={`px-[30px] sm:px-[20px] py-[18px]`}>
                                        <div className={'flex justify-between items-center'}>
                                            <Text className={'text-h3 flex-1 text-gray01 dark:text-dark_gray01'}>
                                                {t('globalNotification.title')}
                                            </Text>
                                            <WizIconButton
                                                className={'p-[6px] '}
                                                onClick={() => {
                                                    createLog('event', 'navigation_click_notification_setting')
                                                    goNotification()
                                                }}>
                                                <IconSetting
                                                    width={20}
                                                    className={
                                                        'cursor-pointer md:hidden fill-gray02 dark:fill-dark_gray02'
                                                    }
                                                />
                                            </WizIconButton>

                                            <WizIconButton onClick={() => setIsOpen(false)}>
                                                <IconClose
                                                    className={
                                                        'w-[20px] fill-gray02 dark:fill-dark_gray02 cursor-pointer hidden md:block'
                                                    }
                                                />
                                            </WizIconButton>
                                        </div>
                                    </div>
                                    <div>
                                        {isNotEmptyArray(notificationList) ? (
                                            <div
                                                className={
                                                    'pt-[2px] px-[30px] sm:px-[10px] max-h-[60vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden not_bg_scrollbar'
                                                }>
                                                <div className={'flex justify-end'}>
                                                    <Text
                                                        onClick={onClickReadAll}
                                                        className={
                                                            'text-ti3 text-gray02 dark:text-dark_gray02 underline cursor-pointer'
                                                        }>
                                                        {t('globalNotification.allRead')}
                                                    </Text>
                                                </div>
                                                {isNotEmptyArray(newNotificationList) && (
                                                    <>
                                                        <Text
                                                            className={`text-ti3 ml-[5px] text-gray01 dark:text-dark_gray01`}>
                                                            {t('globalNotification.data.new.title')}
                                                        </Text>
                                                        <NewNotificationList data={newNotificationList} />
                                                    </>
                                                )}
                                                {isNotEmptyArray(earlierNotificationList) && (
                                                    <>
                                                        <Text
                                                            className={`text-ti3 mt-[10px] ml-[5px] text-gray01 dark:text-dark_gray01`}>
                                                            {t('globalNotification.data.earlier.title')}
                                                        </Text>
                                                        <EarlierNotificationList data={earlierNotificationList} />
                                                    </>
                                                )}
                                                {!isReachingEnd && (
                                                    <div
                                                        onClick={async () => {
                                                            createLog(
                                                                'event',
                                                                'navigation_click_notification_load_more',
                                                            )
                                                            await fetchNextPage()
                                                        }}
                                                        className={
                                                            'w-full flex justify-center items-center pt-[10px] pb-[20px] cursor-pointer gap-x-[5px]'
                                                        }>
                                                        <Text
                                                            className={'text-gray02 dark:text-dark_gray02 text-body2'}>
                                                            {t('globalNotification.data.more')}
                                                        </Text>
                                                        <IconDown
                                                            width={12}
                                                            className={'fill-gray02 dark:fill-dark_gray02'}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <EmptyNotificationContainer />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </FloatingFocusManager>
                        <Dimmed onClose={() => {}} />
                    </>
                )}
                <DailyReportModal ref={dailyReportModalRef} />
            </>
        </NotificationPopoverContext.Provider>
    )
}

export default NotificationDropdown

const Dimmed = ({onClose}) => {
    return (
        <div
            onClick={onClose}
            className={
                'hidden lg:block fixed w-screen h-screen top-0 left-0 bg-black cursor-pointer bg-opacity-40 z-dropdown'
            }
        />
    )
}
